import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Role } from "../../constants/types";
import { FilterStatus } from ".";
import styled from "@emotion/styled";
import OutlineButton from "../../components/OutlineButton";
import { Operator } from "../../features/operator/operatorSlice";
import { labels } from "../../constants/labels";
import { Button, FormControlLabel, Switch, TextField } from "@mui/material";
import { safeHttpUrl } from "../../utils/safeHttpUrl";

interface Props {
  operator: Operator;
  onApproveButtonPressed: () => void;
  onRejectButtonPressed: () => void;
  onStatusChange: () => void;
  onSalesCoachToggle: () => void;
  onMenuCoachToggle: () => void;
  // Resolves to null on success, or an error message to show inline.
  onMenuCoachPortalUrlSave: (url: string) => Promise<string | null>;
}

const ControlRow: FC<Props> = ({
  operator,
  onApproveButtonPressed,
  onRejectButtonPressed,
  onStatusChange,
  onSalesCoachToggle,
  onMenuCoachToggle,
  onMenuCoachPortalUrlSave,
}: Props) => {
  const user = useSelector((state: RootState) => state.user.user);

  // Menu Coach portal link: a draft beside the toggle, saved explicitly so a
  // half-pasted URL is never written. Blank is a valid value (no account yet).
  const [portalDraft, setPortalDraft] = useState(operator.menuCoachPortalUrl || "");
  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalSaving, setPortalSaving] = useState(false);
  useEffect(() => {
    setPortalDraft(operator.menuCoachPortalUrl || "");
    setPortalError(null);
  }, [operator.menuCoachPortalUrl]);
  const portalDirty = portalDraft.trim() !== (operator.menuCoachPortalUrl || "");
  const savePortalUrl = async () => {
    const trimmed = portalDraft.trim();
    if (trimmed && !safeHttpUrl(trimmed)) {
      setPortalError(
        "Enter a full link starting with http:// or https://, or leave it blank."
      );
      return;
    }
    setPortalError(null);
    setPortalSaving(true);
    const err = await onMenuCoachPortalUrlSave(trimmed);
    setPortalSaving(false);
    if (err) setPortalError(err);
  };
  // Explicit "Remove link" so nobody has to know that a blank save clears it.
  const removePortalUrl = async () => {
    setPortalError(null);
    setPortalSaving(true);
    const err = await onMenuCoachPortalUrlSave("");
    setPortalSaving(false);
    if (err) setPortalError(err);
    else setPortalDraft("");
  };

  const approveAndRejectButtons = useMemo(
    () =>
      user &&
      user["role"] === Role.SuperAdmin &&
      operator.status === FilterStatus.PendingApproval,
    [operator.status, user]
  );

  const enableDisableButtons = useMemo(
    () =>
      user &&
      user["role"] !== Role.Salesperson &&
      operator.status !== FilterStatus.Rejected,
    [operator.status, user]
  );

  const salesCoachToggleVisible = useMemo(
    () =>
      user &&
      (user["role"] === Role.SuperAdmin || user["role"] === Role.Admin) &&
      operator.status !== FilterStatus.Rejected,
    [operator.status, user]
  );

  return (
    <>
      {approveAndRejectButtons ? (
        <ApproveRejectRow>
          <OutlineButton onClick={onApproveButtonPressed}>
            {labels.approve}
          </OutlineButton>
          <OutlineButton onClick={onRejectButtonPressed}>
            {labels.reject}
          </OutlineButton>
        </ApproveRejectRow>
      ) : enableDisableButtons ? (
        <DisableButton
          color="secondary"
          variant="outlined"
          onClick={onStatusChange}
        >
          {operator.isActive ? labels.disable : labels.enable}
        </DisableButton>
      ) : (
        <div style={{ height: 40 }}></div>
      )}
      {salesCoachToggleVisible && (
        <SalesCoachRow>
          <FormControlLabel
            control={
              <Switch
                checked={operator.salesCoachEnabled === true}
                onChange={onSalesCoachToggle}
                color="secondary"
              />
            }
            label="Sales Coach (beta)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={operator.menuCoachEnabled === true}
                onChange={onMenuCoachToggle}
                color="secondary"
              />
            }
            label="Menu Coach"
          />
        </SalesCoachRow>
      )}
      {salesCoachToggleVisible && operator.menuCoachEnabled === true && (
        <MenuCoachSection>
          <SectionTitle>Menu Coach portal</SectionTitle>
          <PortalStatus>
            {operator.menuCoachPortalUrl
              ? "Linked. This operator's dashboard shows an Open Menu Coach button that opens the link below."
              : "Not linked. This operator's dashboard shows the Menu Coach promo and lead form. Paste their portal link to give them an Open Menu Coach button instead."}
          </PortalStatus>
          <TextField
            label="Menu Coach portal URL"
            placeholder="https://knowbie-menu-coach.onrender.com/portal/..."
            value={portalDraft}
            onChange={(e) => setPortalDraft(e.target.value)}
            error={!!portalError}
            helperText={
              portalError ||
              "Copy Portal Link from the Menu Coach admin, paste it here, then Save link."
            }
            size="small"
            fullWidth
            inputProps={{ "aria-label": "Menu Coach portal URL" }}
          />
          <PortalActions>
            <SaveLinkButton
              variant="outlined"
              color="secondary"
              onClick={savePortalUrl}
              disabled={portalSaving || !portalDirty}
            >
              {portalSaving ? "Saving..." : "Save link"}
            </SaveLinkButton>
            {operator.menuCoachPortalUrl ? (
              <SaveLinkButton
                variant="text"
                color="secondary"
                onClick={removePortalUrl}
                disabled={portalSaving}
              >
                Remove link
              </SaveLinkButton>
            ) : null}
          </PortalActions>
        </MenuCoachSection>
      )}
    </>
  );
};

const ApproveRejectRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 48px;
  flex-direction: "row";
  align-self: "flex-start";
  gap: 12px;
`;

const DisableButton = styled(Button)`
  width: 120px;
  height: 40px;
  margin-top: 48px;
  text-transform: none;
`;

const SalesCoachRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 16px;
`;

const SaveLinkButton = styled(Button)`
  height: 40px;
  white-space: nowrap;
  text-transform: none;
`;

const MenuCoachSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e6e4ee;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #2e1a47;
`;

const PortalStatus = styled.p`
  margin: 0 0 4px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #4b4b63;
`;

const PortalActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-top: 4px;
`;

export default ControlRow;
